import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import ClearIcon from "@mui/icons-material/Clear";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import CloneButton from "components/CloneButton";
import DateRangeFilter from "components/DateRangeFilter";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import SelectProductMultiple from "components/SelectProductMultiple";
import SelectProductVersionMultiple from "components/SelectProductVersionMultiple";
import SelectSkuStatusMultiple from "components/SelectSkuStatusMultiple";
import SelectSkuFormatMultiple from "components/SelectSkuFormatMultiple";
import SelectSkuAvailabilityMultiple from "components/SelectSkuAvailabilityMultiple";
import SelectQualificationMultiple from "components/SelectQualificationMultiple";
import SelectProfessionMultiple from "components/SelectProfessionMultiple";
import SelectSkuApprovalStatus from "components/SelectSkuApprovalStatus";
import SelectTagMultiple from "components/SelectTagMultiple";
import { isValid } from "date-fns";
import useGlobalContext from "hooks/globalData";
import useDebounce from "hooks/useDebounce";
import SkuResourceActionButtons from "pages/SkusPage/SkuDetailTables/SkuResourceActionButtons";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  GetSkusRequest,
  ListRequestOfBaseFilter,
  ListResponseOfSkuListItem,
  ListResponseOfSkuStatus,
  SkuFilter,
  SkuListItem,
} from "services/KnowfullyAdminClient";
import persistanceStore from "stores/persistanceStore";
import { FilterPages } from "types/common-types";
import { convertToUTC } from "utils/dateUtils";
import CloneSkuConfirmationDialog from "./CloneSkuConfirmationDialog";
import useApprovalStatusFilter from "./useApprovalStatusFilter";

type FilterableSkuListProps = {
  preSelectedProductId?: string;
  preSelectedQualificationIds?: string[];
  resourceId?: string;
  lazyTableTitle?: string;
  organizationId?: string;
  columns?: SkuListColumn[];
  filters?: SkuListFilter[];
};

export enum SkuListColumn {
  Sku = "sku",
  Title = "title",
  SkuFormat = "skuFormat",
  SkuAvailability = "skuAvailability",
  Status = "status",
  CloneSku = "cloneSku",
  SkuResourceActions = "skuResourceActions",
}

const defaultColumns = [
  SkuListColumn.Sku,
  SkuListColumn.Title,
  SkuListColumn.SkuFormat,
  SkuListColumn.SkuAvailability,
  SkuListColumn.Status,
  SkuListColumn.CloneSku,
];

export enum SkuListFilter {
  Search = "search",
  Dates = "dates",
  Product = "product",
  ProductVersion = "productVersion",
  SkuStatus = "skuStatus",
  SkuFormat = "skuFormat",
  SkuAvailability = "skuAvailability",
  Qualification = "qualification",
  Profession = "profession",
  Price = "price",
  Credits = "credits",
  ReleaseDateRange = "releaseDateRange",
  OfflineDateRange = "offlineDateRange",
  CreditSubmissionExpirationDateRange = "creditSubmissionExpirationDateRange",
  ExpirationDateRange = "expirationDateRange",
  ApprovalStatus = "approvalStatus",
  Tags = "tags",
}

const defaultFilters = [
  SkuListFilter.Search,
  SkuListFilter.Dates,
  SkuListFilter.Product,
  SkuListFilter.ProductVersion,
  SkuListFilter.SkuStatus,
  SkuListFilter.SkuFormat,
  SkuListFilter.SkuAvailability,
  SkuListFilter.Qualification,
  SkuListFilter.Profession,
  SkuListFilter.Price,
  SkuListFilter.Credits,
  SkuListFilter.ReleaseDateRange,
  SkuListFilter.OfflineDateRange,
  SkuListFilter.CreditSubmissionExpirationDateRange,
  SkuListFilter.ExpirationDateRange,
];

const styles = {
  hidden: {
    display: "none",
  },
};

const FilterableSkuList = ({
  preSelectedProductId,
  preSelectedQualificationIds,
  resourceId,
  lazyTableTitle = "menubarSkus",
  organizationId,
  columns = defaultColumns,
  filters = defaultFilters,
}: FilterableSkuListProps) => {
  const { formatMessage: t } = useIntl();
  const navigate = useNavigate();
  const { skuService, skuDetailsUrl } = useGlobalContext();
  const [order, setOrder] = useState<TableSortType>("asc");
  const [orderBy, setOrderBy] = useState("sku");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [skuIdToClone, setSkuIdToClone] = useState<string>();

  const searchTerm = persistanceStore.useState((s) => s.skuFilterSearchTerm);
  const trimmedSearchTerm = searchTerm.trim();
  const debouncedSearchTerm = useDebounce(trimmedSearchTerm, 500);

  const minPrice = persistanceStore.useState((s) => s.skuMinPrice);
  const maxPrice = persistanceStore.useState((s) => s.skuMaxPrice);
  const debouncedMinPrice = useDebounce(minPrice, 500);
  const debouncedMaxPrice = useDebounce(maxPrice, 500);

  const totalCreditsMin = persistanceStore.useState((s) => s.skuCreditMin);
  const totalCreditsMax = persistanceStore.useState((s) => s.skuCreditMax);
  const debouncedCreditsMin = useDebounce(totalCreditsMin, 500);
  const debouncedCreditsMax = useDebounce(totalCreditsMax, 500);

  const eventStartDateSearch = persistanceStore.useState(
    (s) => s.skuEventStartDate
  );
  const eventEndDateSearch = persistanceStore.useState(
    (s) => s.skuEventEndDate
  );

  const releaseDateFrom = persistanceStore.useState(
    (s) => s.skuReleaseDateFrom
  );
  const releaseDateTo = persistanceStore.useState((s) => s.skuReleaseDateTo);
  const offlineDateFrom = persistanceStore.useState(
    (s) => s.skuOfflineDateFrom
  );
  const offlineDateTo = persistanceStore.useState((s) => s.skuOfflineDateTo);
  const creditSubmissionExpirationDateFrom = persistanceStore.useState(
    (s) => s.skuCreditSubmissionExpirationDateFrom
  );
  const creditSubmissionExpirationDateTo = persistanceStore.useState(
    (s) => s.skuCreditSubmissionExpirationDateTo
  );
  const expirationDateFrom = persistanceStore.useState(
    (s) => s.skuExpirationDateFrom
  );
  const expirationDateTo = persistanceStore.useState(
    (s) => s.skuExpirationDateTo
  );

  const productIds = persistanceStore.useState((s) => s.productIds ?? []);
  const productVersionIds = persistanceStore.useState(
    (s) => s.productVersionIds ?? []
  );
  const skuStatusIds = persistanceStore.useState((s) => s.skuStatusIds ?? []);
  const skuFormatIds = persistanceStore.useState((s) => s.skuFormatIds ?? []);
  const skuAvailabilityIds = persistanceStore.useState(
    (s) => s.skuAvailabilityIds ?? []
  );
  const skuFilterQualificationIds = persistanceStore.useState(
    (s) => s.skuFilterQualificationIds ?? []
  );
  const skuQualificationProfessionIds = persistanceStore.useState(
    (s) => s.skuQualificationProfessionIds ?? []
  );
  const skuTagIds = persistanceStore.useState((s) => s.skuTagIds ?? []);
  // Tags are scoped to the header organization, so a selection does not carry over to another one.
  const previousOrganizationId = useRef(organizationId);
  useEffect(() => {
    if (previousOrganizationId.current === organizationId) return;
    previousOrganizationId.current = organizationId;
    persistanceStore.update((s) => {
      s.skuTagIds = [];
    });
  }, [organizationId]);
  const {
    approvalStatusQueryDate,
    distinctPreSelectedQualificationIds,
    effectiveApprovalStatus,
    effectiveQualificationIds,
    isApprovalStatusApplicable,
    setApprovalStatus,
  } = useApprovalStatusFilter({
    enabled: filters.includes(SkuListFilter.ApprovalStatus),
    preSelectedQualificationIds,
    selectedQualificationIds: skuFilterQualificationIds,
  });

  const {
    isError,
    data,
    error,
    isFetching,
    refetch,
  } = useQuery<ListResponseOfSkuListItem>(
    [
      "skusList",
      page,
      rowsPerPage,
      order,
      orderBy,
      debouncedSearchTerm,
      debouncedMinPrice,
      debouncedMaxPrice,
      debouncedCreditsMin,
      debouncedCreditsMax,
      eventStartDateSearch,
      eventEndDateSearch,
      releaseDateFrom,
      releaseDateTo,
      offlineDateFrom,
      offlineDateTo,
      creditSubmissionExpirationDateFrom,
      creditSubmissionExpirationDateTo,
      expirationDateFrom,
      expirationDateTo,
      productVersionIds,
      skuStatusIds,
      skuFormatIds,
      skuAvailabilityIds,
      skuFilterQualificationIds,
      resourceId,
      distinctPreSelectedQualificationIds,
      productIds,
      preSelectedProductId,
      organizationId,
      skuQualificationProfessionIds,
      skuTagIds,
      effectiveApprovalStatus,
      approvalStatusQueryDate,
    ],
    () =>
      skuService.getSkus(
        new GetSkusRequest({
          organizationId,
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: new SkuFilter({
            searchText:
              debouncedSearchTerm === "" ? undefined : debouncedSearchTerm,
            productIds: preSelectedProductId
              ? [preSelectedProductId]
              : productIds.length > 0
              ? productIds
              : undefined,
            productVersionIds:
              productVersionIds.length > 0 ? productVersionIds : undefined,
            skuStatusIds: skuStatusIds.length > 0 ? skuStatusIds : undefined,
            skuFormatIds: skuFormatIds.length > 0 ? skuFormatIds : undefined,
            skuAvailabilityIds:
              skuAvailabilityIds.length > 0 ? skuAvailabilityIds : undefined,
            qualificationIds:
              effectiveQualificationIds.length > 0
                ? effectiveQualificationIds
                : undefined,
            approvalStatus: effectiveApprovalStatus,
            resourceId: resourceId,
            professionIds:
              skuQualificationProfessionIds.length > 0
                ? skuQualificationProfessionIds
                : undefined,
            tagIds: skuTagIds.length > 0 ? skuTagIds : undefined,
            minPrice: debouncedMinPrice,
            maxPrice: debouncedMaxPrice,
            minCredits: debouncedCreditsMin,
            maxCredits: debouncedCreditsMax,
            eventStartDate:
              eventStartDateSearch == null || !isValid(eventStartDateSearch)
                ? undefined
                : convertToUTC(eventStartDateSearch),
            eventEndDate:
              eventEndDateSearch == null || !isValid(eventEndDateSearch)
                ? undefined
                : convertToUTC(eventEndDateSearch),
            releaseDateFrom:
              releaseDateFrom == null || !isValid(releaseDateFrom)
                ? undefined
                : convertToUTC(releaseDateFrom),
            releaseDateTo:
              releaseDateTo == null || !isValid(releaseDateTo)
                ? undefined
                : convertToUTC(releaseDateTo),
            offlineDateFrom:
              offlineDateFrom == null || !isValid(offlineDateFrom)
                ? undefined
                : convertToUTC(offlineDateFrom),
            offlineDateTo:
              offlineDateTo == null || !isValid(offlineDateTo)
                ? undefined
                : convertToUTC(offlineDateTo),
            creditSubmissionExpirationDateFrom:
              creditSubmissionExpirationDateFrom == null ||
              !isValid(creditSubmissionExpirationDateFrom)
                ? undefined
                : convertToUTC(creditSubmissionExpirationDateFrom),
            creditSubmissionExpirationDateTo:
              creditSubmissionExpirationDateTo == null ||
              !isValid(creditSubmissionExpirationDateTo)
                ? undefined
                : convertToUTC(creditSubmissionExpirationDateTo),
            expirationDateFrom:
              expirationDateFrom == null || !isValid(expirationDateFrom)
                ? undefined
                : convertToUTC(expirationDateFrom),
            expirationDateTo:
              expirationDateTo == null || !isValid(expirationDateTo)
                ? undefined
                : convertToUTC(expirationDateTo),
          }),
        })
      ),
    { keepPreviousData: true, enabled: !!organizationId }
  );

  const fetchedSkuStatuses = useQuery<ListResponseOfSkuStatus>(
    ["skuService.getSkuStatuses"],
    () =>
      skuService.getSkuStatuses(
        ListRequestOfBaseFilter.fromJS({
          sortField: "displayOrder",
        })
      )
  );

  const onClick = (id: string) => {
    navigate(skuDetailsUrl(id), { state: FilterPages.SkuQualificationList });
  };

  useEffect(() => {
    setPage(0);
    refetch();
  }, [
    debouncedSearchTerm,
    debouncedMinPrice,
    debouncedMaxPrice,
    debouncedCreditsMin,
    debouncedCreditsMax,
    eventStartDateSearch,
    eventEndDateSearch,
    preSelectedProductId,
    productVersionIds,
    skuStatusIds,
    skuFormatIds,
    skuAvailabilityIds,
    skuQualificationProfessionIds,
    skuTagIds,
    skuFilterQualificationIds,
    productIds,
    refetch,
  ]);

  useEffect(() => {
    const resetHiddenFilters = () => {
      if (!filters.includes(SkuListFilter.Search)) {
        persistanceStore.update((s) => (s.skuFilterSearchTerm = ""));
      }
      if (!filters.includes(SkuListFilter.Dates)) {
        persistanceStore.update((s) => {
          s.skuEventStartDate = undefined;
          s.skuEventEndDate = undefined;
        });
      }
      if (!filters.includes(SkuListFilter.Product)) {
        persistanceStore.update((s) => (s.productIds = undefined));
      }
      if (!filters.includes(SkuListFilter.ProductVersion)) {
        persistanceStore.update((s) => (s.productVersionIds = undefined));
      }
      if (!filters.includes(SkuListFilter.SkuStatus)) {
        persistanceStore.update((s) => (s.skuStatusIds = undefined));
      }
      if (!filters.includes(SkuListFilter.SkuFormat)) {
        persistanceStore.update((s) => (s.skuFormatIds = undefined));
      }
      if (!filters.includes(SkuListFilter.SkuAvailability)) {
        persistanceStore.update((s) => (s.skuAvailabilityIds = undefined));
      }
      if (!filters.includes(SkuListFilter.Qualification)) {
        persistanceStore.update(
          (s) => (s.skuFilterQualificationIds = undefined)
        );
      }
      if (!filters.includes(SkuListFilter.Profession)) {
        persistanceStore.update(
          (s) => (s.skuQualificationProfessionIds = undefined)
        );
      }
      if (!filters.includes(SkuListFilter.Tags)) {
        persistanceStore.update((s) => (s.skuTagIds = undefined));
      }
      if (!filters.includes(SkuListFilter.Price)) {
        persistanceStore.update((s) => {
          s.skuMinPrice = undefined;
          s.skuMaxPrice = undefined;
        });
      }
      if (!filters.includes(SkuListFilter.Credits)) {
        persistanceStore.update((s) => {
          s.skuCreditMax = undefined;
          s.skuCreditMin = undefined;
        });
      }
    };

    resetHiddenFilters();
  }, [filters]);

  if (isError) {
    return (
      <Alert severity="error">
        <span>{JSON.stringify(error, null, 2)}</span>
      </Alert>
    );
  }

  const setTerm = (term: string) => {
    persistanceStore.update((s) => {
      s.skuFilterSearchTerm = term;
    });
  };

  const SkuStatusView = ({ value }: { value: SkuListItem }) => {
    const skuStatus =
      fetchedSkuStatuses.data?.data?.find(
        (status) => status.id === value.skuStatusId
      )?.name ?? "";

    return <Box>{skuStatus}</Box>;
  };

  const SkuResourceActions = ({ value }: { value: SkuListItem }) => {
    const skuResource = {
      isSkuUserAgreementAccepted: value.isSkuUserAgreementAccepted,
      isSkuUserAgreementPending: value.isSkuUserAgreementPending,
      hasOrganizationUserId: !!value.organizationUserId,
      organizationUserId: value.organizationUserId,
      skuId: value.id,
    };

    return <SkuResourceActionButtons {...skuResource} />;
  };

  const skus = data?.data ?? [];

  const cellsMap = new Map<SkuListColumn, TableCellType>([
    [
      SkuListColumn.Sku,
      {
        accessor: "sku",
        align: "left",
        width: 0,
        type: "text",
        label: t({ id: "skuLabel" }),
      },
    ],
    [
      SkuListColumn.Title,
      {
        accessor: "title",
        align: "left",
        width: 0,
        type: "text",
        label: t({ id: "skuTitleLabel" }),
      },
    ],
    [
      SkuListColumn.SkuFormat,
      {
        accessor: "skuFormat.name",
        align: "left",
        width: 0,
        type: "text",
        label: t({ id: "skuFormatLabel" }),
      },
    ],
    [
      SkuListColumn.SkuAvailability,
      {
        accessor: "skuAvailabilityName",
        customSortAccessor: "skuAvailability.name",
        align: "left",
        width: 0,
        type: "text",
        label: t({ id: "skuAvailabilityLabel" }),
      },
    ],
    [
      SkuListColumn.Status,
      {
        accessor: "value",
        align: "left",
        type: "node",
        width: 0,
        View: SkuStatusView,
        noSort: true,
        label: t({ id: "statusLabel" }),
      },
    ],
    [
      SkuListColumn.CloneSku,
      {
        accessor: "value",
        align: "center",
        width: 0,
        type: "node",
        View: ({ value }) => (
          <CloneButton onClick={() => setSkuIdToClone(value.id)} />
        ),
        noSort: true,
        label: t({ id: "skuCloneLabel" }),
      },
    ],
    [
      SkuListColumn.SkuResourceActions,
      {
        accessor: "value",
        align: "center",
        type: "node",
        width: 0,
        View: SkuResourceActions,
        noSort: true,
        label: t({ id: "agreements" }),
      },
    ],
  ]);

  const cells = columns
    .map((c) => cellsMap.get(c))
    .filter((c) => !!c) as TableCellType[];

  return (
    <>
      <Accordion>
        <AccordionSummary style={{ margin: 0 }} expandIcon={<ExpandMoreIcon />}>
          <Typography>
            <T id="filtersLabel" />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box mb={3} width="100%">
            <Grid container spacing={2}>
              <Grid
                item
                xs={11}
                sm={12}
                md={6}
                sx={
                  filters.includes(SkuListFilter.Search) ? null : styles.hidden
                }
              >
                <TextField
                  fullWidth
                  variant="outlined"
                  value={searchTerm ?? ""}
                  placeholder={t({ id: "searchPlaceholder" })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setTerm("")}>
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  onChange={(e) => setTerm(e.target.value)}
                />
              </Grid>

              {/* Event Date Range Filter */}
              <Grid
                item
                xs={11}
                sm={12}
                md={6}
                mb={2}
                sx={
                  filters.includes(SkuListFilter.Dates) ? null : styles.hidden
                }
              >
                <DateRangeFilter
                  fromValue={eventStartDateSearch}
                  toValue={eventEndDateSearch}
                  onFromChange={(date) =>
                    persistanceStore.update((s) => {
                      s.skuEventStartDate = date;
                    })
                  }
                  onToChange={(date) =>
                    persistanceStore.update((s) => {
                      s.skuEventEndDate = date;
                    })
                  }
                  disabled={isFetching}
                  fromLabel={t({ id: "skuFormEventStartDateLabel" })}
                  toLabel={t({ id: "skuFormEventEndDateLabel" })}
                />
              </Grid>
              {filters.includes(SkuListFilter.ApprovalStatus) && (
                <Grid item xs={11} sm={12} md={4}>
                  <SelectSkuApprovalStatus
                    value={effectiveApprovalStatus}
                    disabled={!isApprovalStatusApplicable}
                    variant="outlined"
                    onChange={(approvalStatus) => {
                      setPage(0);
                      setApprovalStatus(approvalStatus);
                    }}
                  />
                </Grid>
              )}
              <Grid
                item
                xs={11}
                sm={12}
                md={4}
                sx={
                  filters.includes(SkuListFilter.Product) ? null : styles.hidden
                }
              >
                <SelectProductMultiple
                  value={
                    preSelectedProductId ? [preSelectedProductId] : productIds
                  }
                  onChange={(ids) =>
                    persistanceStore.update((s) => {
                      s.productIds = ids;
                    })
                  }
                  organizationId={organizationId}
                  disabled={!!preSelectedProductId}
                  variant="outlined"
                />
              </Grid>
              <Grid
                item
                xs={11}
                sm={12}
                md={4}
                sx={
                  filters.includes(SkuListFilter.ProductVersion)
                    ? null
                    : styles.hidden
                }
              >
                <SelectProductVersionMultiple
                  value={productVersionIds}
                  onChange={(ids) =>
                    persistanceStore.update((s) => {
                      s.productVersionIds = ids;
                    })
                  }
                  variant="outlined"
                />
              </Grid>
              <Grid
                item
                xs={11}
                sm={12}
                md={4}
                sx={
                  filters.includes(SkuListFilter.SkuStatus)
                    ? null
                    : styles.hidden
                }
              >
                <SelectSkuStatusMultiple
                  value={skuStatusIds}
                  onChange={(ids) =>
                    persistanceStore.update((s) => {
                      s.skuStatusIds = ids;
                    })
                  }
                  variant="outlined"
                />
              </Grid>
              <Grid
                item
                xs={11}
                sm={12}
                md={4}
                sx={
                  filters.includes(SkuListFilter.SkuFormat)
                    ? null
                    : styles.hidden
                }
              >
                <SelectSkuFormatMultiple
                  value={skuFormatIds}
                  onChange={(ids) =>
                    persistanceStore.update((s) => {
                      s.skuFormatIds = ids;
                    })
                  }
                  variant="outlined"
                />
              </Grid>
              <Grid
                item
                xs={11}
                sm={12}
                md={4}
                sx={
                  filters.includes(SkuListFilter.SkuAvailability)
                    ? null
                    : styles.hidden
                }
              >
                <SelectSkuAvailabilityMultiple
                  value={skuAvailabilityIds}
                  onChange={(ids) =>
                    persistanceStore.update((s) => {
                      s.skuAvailabilityIds = ids;
                    })
                  }
                  variant="outlined"
                />
              </Grid>

              <Grid
                item
                xs={11}
                sm={12}
                md={4}
                sx={
                  filters.includes(SkuListFilter.Profession)
                    ? null
                    : styles.hidden
                }
              >
                <SelectProfessionMultiple
                  value={skuQualificationProfessionIds}
                  onChange={(ids) =>
                    persistanceStore.update((s) => {
                      s.skuQualificationProfessionIds = ids;
                    })
                  }
                  variant="outlined"
                />
              </Grid>
              {filters.includes(SkuListFilter.Tags) && (
                <Grid item xs={11} sm={12} md={4}>
                  <SelectTagMultiple
                    organizationId={organizationId}
                    value={skuTagIds}
                    onChange={(ids) =>
                      persistanceStore.update((s) => {
                        s.skuTagIds = ids;
                      })
                    }
                  />
                </Grid>
              )}
              <Grid
                item
                xs={11}
                sm={12}
                md={12}
                sx={
                  filters.includes(SkuListFilter.Qualification)
                    ? null
                    : styles.hidden
                }
              >
                <SelectQualificationMultiple
                  value={
                    distinctPreSelectedQualificationIds &&
                    distinctPreSelectedQualificationIds.length > 0
                      ? distinctPreSelectedQualificationIds
                      : skuFilterQualificationIds
                  }
                  onChange={(ids) =>
                    persistanceStore.update((s) => {
                      s.skuFilterQualificationIds = ids;
                    })
                  }
                  disabled={
                    !!distinctPreSelectedQualificationIds &&
                    distinctPreSelectedQualificationIds.length > 0
                  }
                  variant="outlined"
                />
              </Grid>
              <Grid
                item
                xs={11}
                sm={12}
                md={3}
                sx={
                  filters.includes(SkuListFilter.Price) ? null : styles.hidden
                }
              >
                <TextField
                  fullWidth
                  type="number"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoneyIcon />
                      </InputAdornment>
                    ),
                  }}
                  variant="outlined"
                  value={minPrice == null ? "" : minPrice}
                  placeholder={t({ id: "minPriceMessage" })}
                  onChange={(e) =>
                    persistanceStore.update((s) => {
                      s.skuMinPrice = parseInt(e.target.value);
                    })
                  }
                />
              </Grid>
              <Grid
                item
                xs={11}
                sm={12}
                md={3}
                sx={
                  filters.includes(SkuListFilter.Price) ? null : styles.hidden
                }
              >
                <TextField
                  fullWidth
                  type="number"
                  variant="outlined"
                  value={maxPrice == null ? "" : maxPrice}
                  placeholder={t({ id: "maxPriceMessage" })}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoneyIcon />
                      </InputAdornment>
                    ),
                  }}
                  onChange={(e) =>
                    persistanceStore.update((s) => {
                      s.skuMaxPrice = parseInt(e.target.value);
                    })
                  }
                />
              </Grid>
              <Grid
                item
                xs={11}
                sm={12}
                md={3}
                sx={
                  filters.includes(SkuListFilter.Credits) ? null : styles.hidden
                }
              >
                <TextField
                  fullWidth
                  type="number"
                  variant="outlined"
                  value={totalCreditsMin == null ? "" : totalCreditsMin}
                  placeholder={t({ id: "totalCreditsMinMessage" })}
                  onChange={(e) =>
                    persistanceStore.update((s) => {
                      s.skuCreditMin = parseInt(e.target.value);
                    })
                  }
                />
              </Grid>
              <Grid
                item
                xs={11}
                sm={12}
                md={3}
                sx={
                  filters.includes(SkuListFilter.Credits) ? null : styles.hidden
                }
              >
                <TextField
                  fullWidth
                  type="number"
                  variant="outlined"
                  value={totalCreditsMax == null ? "" : totalCreditsMax}
                  placeholder={t({ id: "totalCreditsMaxMessage" })}
                  onChange={(e) =>
                    persistanceStore.update((s) => {
                      s.skuCreditMax = parseInt(e.target.value);
                    })
                  }
                />
              </Grid>
              <Grid
                item
                xs={11}
                sm={12}
                md={6}
                mb={2}
                sx={
                  filters.includes(SkuListFilter.ReleaseDateRange)
                    ? null
                    : styles.hidden
                }
              >
                <DateRangeFilter
                  label={t({ id: "skuFormReleaseDateLabel" })}
                  fromValue={releaseDateFrom}
                  toValue={releaseDateTo}
                  onFromChange={(date) =>
                    persistanceStore.update((s) => {
                      s.skuReleaseDateFrom = date;
                    })
                  }
                  onToChange={(date) =>
                    persistanceStore.update((s) => {
                      s.skuReleaseDateTo = date;
                    })
                  }
                  disabled={isFetching}
                />
              </Grid>

              <Grid
                item
                xs={11}
                sm={12}
                md={6}
                mb={2}
                sx={
                  filters.includes(SkuListFilter.OfflineDateRange)
                    ? null
                    : styles.hidden
                }
              >
                <DateRangeFilter
                  label={t({ id: "skuFormOfflineDateLabel" })}
                  fromValue={offlineDateFrom}
                  toValue={offlineDateTo}
                  onFromChange={(date) =>
                    persistanceStore.update((s) => {
                      s.skuOfflineDateFrom = date;
                    })
                  }
                  onToChange={(date) =>
                    persistanceStore.update((s) => {
                      s.skuOfflineDateTo = date;
                    })
                  }
                  disabled={isFetching}
                />
              </Grid>
              <Grid
                item
                xs={11}
                sm={12}
                md={6}
                mb={2}
                sx={
                  filters.includes(
                    SkuListFilter.CreditSubmissionExpirationDateRange
                  )
                    ? null
                    : styles.hidden
                }
              >
                <DateRangeFilter
                  label={t({
                    id: "skuFormCreditSubmissionExpirationDateLabel",
                  })}
                  fromValue={creditSubmissionExpirationDateFrom}
                  toValue={creditSubmissionExpirationDateTo}
                  onFromChange={(date) =>
                    persistanceStore.update((s) => {
                      s.skuCreditSubmissionExpirationDateFrom = date;
                    })
                  }
                  onToChange={(date) =>
                    persistanceStore.update((s) => {
                      s.skuCreditSubmissionExpirationDateTo = date;
                    })
                  }
                  disabled={isFetching}
                />
              </Grid>

              <Grid
                item
                xs={11}
                sm={12}
                md={6}
                mb={2}
                sx={
                  filters.includes(SkuListFilter.ExpirationDateRange)
                    ? null
                    : styles.hidden
                }
              >
                <DateRangeFilter
                  label={t({ id: "skuFormExpirationDateLabel" })}
                  fromValue={expirationDateFrom}
                  toValue={expirationDateTo}
                  onFromChange={(date) =>
                    persistanceStore.update((s) => {
                      s.skuExpirationDateFrom = date;
                    })
                  }
                  onToChange={(date) =>
                    persistanceStore.update((s) => {
                      s.skuExpirationDateTo = date;
                    })
                  }
                  disabled={isFetching}
                />
              </Grid>
            </Grid>
          </Box>
        </AccordionDetails>
      </Accordion>

      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: skus,
          loading: isFetching,
          title: lazyTableTitle ? t({ id: lazyTableTitle }) : undefined,

          dense: true,
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onClick,
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) => skuDetailsUrl(id)),
          ],
        }}
      />
      <CloneSkuConfirmationDialog
        skuId={skuIdToClone}
        close={() => setSkuIdToClone(undefined)}
      />
    </>
  );
};

export default FilterableSkuList;
